import React, { ReactElement } from 'react'

import { BenefitType } from '../../model/remuneration'
import {
  decorateAnyFieldSignature,
  getAnyFieldErrorSignature,
  getFieldValueSignature,
  setAnyFieldValueSignature,
  setFieldValueSignature,
} from '../../utils/form-utils'
import { t, tx } from '../../utils/translation-utils'
import Form from '../antd/form'
import Col from '../elements/grid/col'
import Row from '../elements/grid/row'
import Headline from '../elements/Headline'
import Icon from '../elements/icon'
import Input from '../elements/input'
import Subcard from '../elements/Subcard'

type BenefitOtherReportingFields = {
  taxReportings: {
    title: string
    type: BenefitType
    reportingAmount: string
  }[]
}

type Props<Fields extends BenefitOtherReportingFields> = {
  decorateAnyField: decorateAnyFieldSignature<Fields>
  getFieldValue: getFieldValueSignature<Fields>
  setFieldValue: setFieldValueSignature<Fields>
  setAnyFieldValue: setAnyFieldValueSignature
  getAnyFieldError: getAnyFieldErrorSignature
}

export default function BenefitTaxReportingForm<Fields extends BenefitOtherReportingFields>(
  props: Props<Fields>
): ReactElement | null {
  const add = () => {
    const { getFieldValue, setAnyFieldValue } = props
    const taxReportings = getFieldValue('taxReportings')
    setAnyFieldValue(`taxReportings.${taxReportings.length}`, {
      type: 'Tax Reporting',
    })
  }
  const remove = (i: number) => {
    const { getFieldValue, setFieldValue } = props
    const taxReportings = getFieldValue('taxReportings')
    taxReportings.splice(i, 1)
    setFieldValue('taxReportings', taxReportings)
  }

  const { decorateAnyField, getFieldValue, getAnyFieldError } = props

  return (
    <div>
      <Row style={{ marginTop: '15px', marginBottom: '20px' }}>
        <Col span={24}>
          <Headline>{t('benefit.form.tax_reporting.intro.title')}</Headline>
        </Col>
        <Col span={24}>
          <p>{t('benefit.form.tax_reporting.intro.line_1')}</p>
          <p>
            {t('benefit.form.tax_reporting.intro.line_2')}
            <ol>
              <li>{t('benefit.form.tax_reporting.intro.line_2.items.1')}</li>
              <li>{t('benefit.form.tax_reporting.intro.line_2.items.2')}</li>
              <li>{t('benefit.form.tax_reporting.intro.line_2.items.3')}</li>
              <li>{t('benefit.form.tax_reporting.intro.line_2.items.4')}</li>
              <li>{t('benefit.form.tax_reporting.intro.line_2.items.5')}</li>
              <li>{t('benefit.form.tax_reporting.intro.line_2.items.6')}</li>
            </ol>
          </p>
          <p>
            {tx('benefit.form.tax_reporting.intro.line_3', {
              link: (
                <a
                  href="https://skat.dk/erhverv/ansatte-og-loen/personalegoder-naar-du-er-arbejdsgiver/skat-af-personalegoder"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('benefit.form.tax_reporting.intro.line_3.link')}
                </a>
              ),
            })}
          </p>
        </Col>
      </Row>
      <Subcard>
        <Row style={{ marginBottom: '-10px' }}>
          <Col span={16}>
            <label>{t('benefit.form.tax_reporting.header.title')}</label>
          </Col>
          <Col span={8}>
            <label>{t('benefit.form.tax_reporting.header.reporting_amount')}</label>
          </Col>
        </Row>
        {getFieldValue('taxReportings').map((benefit, i) => {
          return (
            <Form.Item
              key={i}
              validateStatus={
                getAnyFieldError(`taxReportings.${i}.title`) || getAnyFieldError(`taxReportings.${i}.reportingAmount`)
                  ? 'error'
                  : 'success'
              }
            >
              <Row>
                <Col span={14}>
                  {decorateAnyField(`taxReportings.${i}.title`, {
                    placeholder: t('benefit.form.tax_reporting.title'),
                    validate: (val: string | undefined) =>
                      !val ? t('benefit.form.tax_reporting.title.required') : null,
                    skipWrapper: true,
                    skipLabel: true,
                  })(<Input />)}
                </Col>
                <Col span={9}>
                  {decorateAnyField(`taxReportings.${i}.reportingAmount`, {
                    placeholder: t('benefit.form.tax_reporting.reporting_amount'),
                    suffix: t('benefit.form.tax_reporting.reporting_amount.suffix'),
                    validate: (val: string | undefined) =>
                      !val ? t('benefit.form.tax_reporting.reporting_amount.required') : null,
                    skipWrapper: true,
                    skipLabel: true,
                  })(<Input />)}
                </Col>
                <Col span={1} className="contracts-remove-row">
                  <span onClick={() => remove(i)}>
                    <Icon type="xSign" />
                  </span>
                </Col>
              </Row>
            </Form.Item>
          )
        })}
        <span className="contracts-add-row" onClick={add}>
          <Icon type="plusCircle" />{' '}
          {getFieldValue('taxReportings').length > 0
            ? t('benefit.form.tax_reporting.add_row.not_empty')
            : t('benefit.form.tax_reporting.add_row.empty')}
        </span>
      </Subcard>
    </div>
  )
}
